import React, {
    useCallback, useEffect, useMemo, useState, FC,
} from 'react';
import uniqBy from 'lodash/uniqBy';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { date, number, object } from 'yup';
import moment, { Moment } from 'moment';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';

import {
    INITIAL_START_DATE_FOR_CLIENT_REPORT_DATEPICKER,
    languages,
    migrationYearOf3b,
    PRODUCT_VALUES_FOR_3B,
} from 'constants/constants';

import {
    DatePicker,
    Infobox,
    Loader,
    notification,
    Select,
} from 'ui-library';
import Card from '../../../../components/Card/Card';
import { getClientId } from '../../../../utils';
import { useClientLayout } from '../../hooks/useClientLayout';
import ButtonsBlockRow from '../../../../components/ButtonsBlockRow';
import {
    compareDates,
    dateFormatZurich,
    formatDateWithoutLocalConversion,
} from '../../../../utils/datetime';
import { useYupValidationResolver } from '../../../../hooks/useYupValidationResolver';
import ServiceManager from '../../../../services/ServiceManager';

import { useClientProfile } from '../../../../prodivers/clientProfile';
import { ACTIVE } from '../../../../constants/portfolioStatuses';
import './ClientReports.css';

// --- Interfaces ---

interface FormValues {
    fromDate: Date | Moment | string | null;
    toDate: Date | Moment | string | null;
    product: number | null;
}

interface StartDateResponse {
    minimumStartDate: string;
    isMigrated: boolean;
    isInvestementAvailable: boolean;
}

const currentYear = format(new Date(), 'yyyy');

const ClientReports: FC = () => {
    const {
        t,
        i18n: { language },
    } = useTranslation();

    const { dfsClientId } = useParams<{ dfsClientId: string }>();
    const clientId = getClientId(dfsClientId);

    const [isDataSending, setDataSending] = useState<boolean>(false);
    const [isStartDateLoading, setStartDateLoading] = useState<boolean>(false);
    const [generalError, setGeneralError] = useState<string | boolean>(false);

    const { sectionedPortfolios, isOverviewLoading } = useClientProfile();

    const allPortfolios = useMemo(
        () => [
            ...(sectionedPortfolios?.jointRegularPortfolios ?? []),
        ],
        [sectionedPortfolios],
    );

    const productOptions = useMemo(
        () => uniqBy(uniqBy(
            allPortfolios?.filter((f) => f.Status?.value === ACTIVE),
            (u) => u.ProductId,
        )?.map((i) => {
            const productOption = PRODUCT_VALUES_FOR_3B.includes(i?.ProductId) ? '3b' : i.Product;

            return {
                label: productOption,
                value: i.ProductId,
            };
        }), 'label'),
        [allPortfolios],
    );

    const today = moment.utc().startOf('day');

    const [startDate, setStartDate] = useState<string>(INITIAL_START_DATE_FOR_CLIENT_REPORT_DATEPICKER);
    const [is3BProduct, setIs3BProduct] = useState<boolean>(false);
    const [isMigrated, setIsMigrated] = useState<boolean>(true);
    const [isInvestementAvailable, setIsInvestementAvailable] = useState<boolean>(true);

    const isFromDateDisabled = useMemo(
        () => isStartDateLoading
        || (is3BProduct && isMigrated && currentYear === migrationYearOf3b)
        || (!isMigrated && !isInvestementAvailable),
        [isStartDateLoading, is3BProduct, isMigrated, isInvestementAvailable],
    );

    const isToDateDisabled = useMemo(
        () => isStartDateLoading
        || (!isMigrated && !isInvestementAvailable),
        [isStartDateLoading, isMigrated, isInvestementAvailable],
    );

    const schema = useMemo(
        () => object().shape({
            fromDate: date().required(t('validation.mandatoryField')),
            toDate: date().required(t('validation.mandatoryField')),
            product: number()
                .required(t('validation.mandatoryField'))
                .nullable(),
        }),
        [t],
    );

    const resolver = useYupValidationResolver(schema);

    const {
        handleSubmit,
        formState: { errors },
        control,
        watch,
        setValue,
        getValues,
    } = useForm<FormValues>({
        resolver,
        defaultValues: {
            fromDate: moment.utc().startOf('year'),
            toDate: today,
            product: null,
        },
    });

    const formData = watch();

    useClientLayout();

    const handleSetIs3BProduct = useCallback((value: number) => {
        const is3BProductBool = PRODUCT_VALUES_FOR_3B.includes(value as any);

        setIs3BProduct(is3BProductBool);
    }, []);

    const fetchStartDate = useCallback(async () => {
        if (!clientId || !formData.product) return;

        try {
            setStartDateLoading(true);
            const response = await ServiceManager.customReportsService(
                'getReportsStartDate',
                [{ contactId: clientId, productId: formData.product }],
            ) as any;

            const { minimumStartDate, isMigrated: migrated, isInvestementAvailable: investmentAvailable } = response?.data as StartDateResponse;

            const { fromDate, toDate } = getValues();

            // Product selection keeps the chosen dates; only raise them to the minimum start date
            if (!fromDate || compareDates(minimumStartDate, fromDate)) {
                setValue('fromDate', minimumStartDate);
            }
            if (compareDates(minimumStartDate, toDate)) {
                setValue('toDate', today);
            }

            setStartDate(minimumStartDate);
            setIsMigrated(migrated);
            setIsInvestementAvailable(investmentAvailable);
        } catch (err: any) {
            notification.open({ content: `${err.message}!`, type: 'error' });
        } finally {
            setStartDateLoading(false);
        }
    }, [clientId, formData.product, getValues, setValue, today]);

    useEffect(() => {
        fetchStartDate();
    }, [clientId, formData.product]);

    const generateReport = useCallback(async (values: FormValues) => {
        try {
            setDataSending(true);
            await ServiceManager.customReportsService('generateReport', [
                {
                    data: {
                        fromDate: formatDateWithoutLocalConversion(
                            values.fromDate,
                            moment.defaultFormatUtc,
                        ),
                        toDate: formatDateWithoutLocalConversion(
                            values.toDate,
                            moment.defaultFormatUtc,
                        ),
                        language: (languages as any)?.[language] ?? 0,
                        contactId: clientId,
                        productId: values.product,
                    },
                },
            ]);
            notification.open({
                content: t('clientReports.generate.successful'),
                type: 'success',
            });
        } catch (e) {
            setGeneralError(t('advisoryDashboard.error'));
        } finally {
            setDataSending(false);
        }
    }, [language, clientId, t]);

    return (
        <div className="container ClientReports mb-40">
            <Card
                title={t('advisoryDashboard.clientReports.title')}
                titleTagName="h1"
            >
                <form className="form" onSubmit={handleSubmit(generateReport)}>
                    <Controller
                        control={control}
                        name="product"
                        render={({ field: { onChange, value, ...field } }) => (
                            <Select
                                {...field}
                                value={value}
                                className="report-products"
                                label={t('clientReports.products.label')}
                                options={productOptions}
                                placeholder={t('position.pleaseSelect')}
                                onChange={(val: number) => {
                                    onChange(val);
                                    handleSetIs3BProduct(val);
                                }}
                                error={errors?.product?.message}
                                required
                                disabled={isOverviewLoading}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="fromDate"
                        render={({ field }) => (
                            <DatePicker
                                {...field}
                                value={field.value ? formatDateWithoutLocalConversion(field.value, dateFormatZurich) : ''}
                                format={dateFormatZurich}
                                data-cy="fromDate"
                                errors={errors?.fromDate}
                                label={t('clientReports.fromDate.label')}
                                disabledDate={(current) => current && (
                                    compareDates(current, formData.toDate)
                                    || compareDates(moment(startDate), current)
                                )}
                                required
                                disabled={isFromDateDisabled}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="toDate"
                        render={({ field }) => (
                            <DatePicker
                                {...field}
                                value={field.value ? formatDateWithoutLocalConversion(field.value, dateFormatZurich) : ''}
                                format={dateFormatZurich}
                                data-cy="toDate"
                                errors={errors?.toDate}
                                label={t('clientReports.toDate.label')}
                                disabledDate={(current) => current && (
                                    compareDates(current, moment())
                                    || compareDates(moment(formData.fromDate), current)
                                )}
                                required
                                disabled={isToDateDisabled}
                            />
                        )}
                    />

                    <Infobox accent>
                        {t('clientReports.createReport.reportGenerationDurationInfoMessage')}
                    </Infobox>

                    {generalError && <Infobox error>{generalError as string}</Infobox>}

                    <ButtonsBlockRow
                        primaryButton={{
                            disabled: isDataSending,
                            text: isDataSending ? <Loader /> : t('clientReports.createReportButton.label'),
                            onClick: handleSubmit(generateReport),
                        }}
                    />
                </form>
            </Card>
        </div>
    );
};

export default ClientReports;
